// src/components/inventory/InventoryProductDetailModal.tsx
// La scheda del prodotto: da qui la merce ESCE, perché usata durante un
// servizio o perché venduta al cliente. Quanti pezzi lo chiede il modalino.
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Boxes, Scissors, ShoppingBag, Edit2, Trash2, Loader2 } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import InventoryQuantityDialog from '@/components/inventory/InventoryQuantityDialog';
import { useInventory } from '@/contexts/InventoryContext';
import { InventoryProduct } from '@/services/inventory/inventoryService';

interface InventoryProductDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: InventoryProduct | null;
    onEdit: () => void;
}

const InventoryProductDetailModal: React.FC<InventoryProductDetailModalProps> = ({
    isOpen,
    onClose,
    product,
    onEdit,
}) => {
    const { t } = useTranslation();
    const { addMovement, removeProduct } = useInventory();

    // Dopo un'eliminazione il prodotto sparisce dall'elenco mentre il modale si
    // sta ancora chiudendo: teniamo l'ultimo per non mostrare un modale vuoto.
    const lastProduct = useRef<InventoryProduct | null>(null);
    if (product) lastProduct.current = product;
    const shown = product ?? lastProduct.current;

    const [pendingType, setPendingType] = useState<'use' | 'sale' | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const stockLow = !!shown && shown.stock <= shown.min_threshold;
    const outOfStock = !shown || shown.stock <= 0;

    const handleMovement = async (quantity: number) => {
        if (!product || !pendingType) return;
        if (quantity > product.stock) {
            toast.error(t('inventory.not_enough_stock', 'Giacenza insufficiente'));
            return;
        }
        setIsSaving(true);
        const res = await addMovement({
            product_id: product.id,
            type: pendingType,
            quantity,
            unit_price: pendingType === 'sale' ? Number(product.price) : null,
        });
        setIsSaving(false);
        if (res.success) {
            toast.success(
                pendingType === 'use'
                    ? t('inventory.use_saved', 'Segnato come usato in servizio')
                    : t('inventory.sale_saved', 'Vendita registrata')
            );
            setPendingType(null);
        } else {
            toast.error(res.error || t('common.error'));
        }
    };

    const handleDelete = async () => {
        if (!product) return;
        setIsSaving(true);
        const res = await removeProduct(product.id);
        setIsSaving(false);
        setConfirmDelete(false);
        if (res.success) {
            toast.success(t('inventory.product_removed', 'Prodotto rimosso dal magazzino'));
            onClose();
        } else {
            toast.error(res.error || t('common.error'));
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                className="max-w-xl"
                headerClassName="bg-white border-b border-gray-100"
                closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
                headerContent={
                    <div className="flex items-center gap-3.5 pr-10">
                        <div className="w-12 h-12 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center flex-shrink-0">
                            <Boxes size={22} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold text-gray-900 truncate">{shown?.name}</h2>
                            <p className="text-sm text-gray-400 truncate">
                                {shown?.category}
                                {shown?.barcode ? ` · ${shown.barcode}` : ''} · €
                                {Number(shown?.price ?? 0).toFixed(2)}
                            </p>
                        </div>
                    </div>
                }
            >
                <div className="space-y-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span
                            className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-bold ${stockLow ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
                                }`}
                        >
                            <Boxes size={15} />
                            {t('inventory.stock', 'Giacenza')}: {shown?.stock ?? 0}
                        </span>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={onEdit}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors"
                            >
                                <Edit2 size={14} />
                                {t('common.edit', 'Modifica')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setConfirmDelete(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-100 bg-red-50 text-red-600 text-sm font-semibold hover:bg-red-100 transition-colors"
                            >
                                <Trash2 size={14} />
                                {t('common.delete', 'Elimina')}
                            </button>
                        </div>
                    </div>

                    {/* Le uscite: il secondario a sinistra, il primario sempre più a destra. */}
                    <div className="flex justify-end gap-2 flex-wrap pt-5 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setPendingType('sale')}
                            disabled={isSaving || outOfStock}
                            className="flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <ShoppingBag size={16} />
                            {t('inventory.sold', 'Venduto (rivendita)')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setPendingType('use')}
                            disabled={isSaving || outOfStock}
                            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-clip-500 hover:bg-clip-600 text-white font-bold text-sm shadow-sm shadow-clip-200 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                        >
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Scissors size={16} />}
                            {t('inventory.used_in_service', 'Usato in servizio')}
                        </button>
                    </div>
                </div>
            </Modal>

            <InventoryQuantityDialog
                isOpen={!!pendingType}
                icon={pendingType === 'sale' ? ShoppingBag : Scissors}
                title={
                    pendingType === 'sale'
                        ? t('inventory.sold_qty_title', 'Quanti ne hai venduti?')
                        : t('inventory.used_qty_title', 'Quanti ne hai usati?')
                }
                subtitle={shown?.name}
                max={Math.max(shown?.stock ?? 0, 0)}
                confirmLabel={t('common.confirm', 'Conferma')}
                loading={isSaving}
                onConfirm={handleMovement}
                onCancel={() => setPendingType(null)}
            />

            <ConfirmDialog
                isOpen={confirmDelete}
                title={t('inventory.delete_title', 'Elimina prodotto')}
                description={t(
                    'inventory.delete_desc',
                    'Il prodotto non sarà più selezionabile. I movimenti già registrati restano nello storico.'
                )}
                confirmLabel={t('common.delete', 'Elimina')}
                cancelLabel={t('common.cancel', 'Annulla')}
                variant="danger"
                loading={isSaving}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete(false)}
            />
        </>
    );
};

export default InventoryProductDetailModal;
